import { FC, type JSX } from 'react';

import { FlowRadioGroup, getRadioGroupNameProps } from '@eneco/flows/components';
import { requiredRadioChoice } from '@eneco/flows/helpers';
import { RadioTile } from '@eneco/sparky';
import { DetachedHouse, SemiDetachedHouse, CornerHouse, TerracedHouse, Apartment } from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { BuildingTypeStepRendering } from '@sitecore/types/BuildingTypeStep';
import * as yup from 'yup';

import { StepShell } from '../../components/StepShell/StepShell';
import { useFlowStep } from '../../hooks';
import { BuildingType } from '../../types';

interface FormFields {
  buildingType: BuildingType;
}

const generateInputElement = (value: string, label: string, description?: string): JSX.Element => {
  let icon: JSX.Element;

  switch (value) {
    case BuildingType.Apartment:
      icon = <Apartment size="small" />;
      break;

    case BuildingType.TerracedHouse:
      icon = <TerracedHouse size="small" />;
      break;

    case BuildingType.CornerHouse:
      icon = <CornerHouse size="small" />;
      break;

    case BuildingType.SemiDetachedHouse:
      icon = <SemiDetachedHouse size="small" />;
      break;

    default:
      icon = <DetachedHouse size="small" />;
  }
  return (
    <RadioTile key={value} value={value} icon={icon} subtitle={description || undefined}>
      {label}
    </RadioTile>
  );
};

export const BuildingTypeStep: FC = () => {
  const { BuildingTypeStep: { fields: textualData } = {} } = usePlaceholderContent<{
    BuildingTypeStep: BuildingTypeStepRendering;
  }>();

  const {
    form: {
      handleSubmit,
      control,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    buildSchema: () =>
      yup.object({
        buildingType: requiredRadioChoice(textualData?.choice?.buildingTypeRadio?.value?.requiredMessage),
      }),
    defaultValues: context => ({ buildingType: context.buildingType }),
    onSubmitSuccess: formData => ({ ...formData }),
  });

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <FlowRadioGroup
        control={control}
        name="buildingType"
        {...getRadioGroupNameProps(textualData?.choice?.buildingTypeRadio?.value?.label)}
        hint={textualData?.choice?.buildingTypeRadio?.value?.hint || undefined}
        direction="column"
        error={errors.buildingType?.message}>
        {textualData?.choice?.buildingTypeRadio?.value?.options?.map(option =>
          generateInputElement(option.name as keyof typeof BuildingType, option.label, option.description),
        )}
      </FlowRadioGroup>
    </StepShell>
  );
};
